import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'react-toastify';
import './AdminPanel.css';

const PLAN_LABELS = { trial: 'Trial', basic: 'Basic', pro: 'Pro' };
const PLAN_OPTIONS = ['trial', 'basic', 'pro'];

export default function AdminPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);

  // Redirect non-admins
  useEffect(() => {
    if (user && !user.is_admin) {
      toast.error('Admin access required');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const loadStats = useCallback(async () => {
    try {
      const res = await api.get('/api/admin/stats');
      setStats(res.data.stats);
    } catch {
      toast.error('Failed to load stats');
    } finally {
      setLoadingStats(false);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const params = new URLSearchParams({ page });
      if (search) params.set('search', search);
      const res = await api.get(`/api/admin/users?${params}`);
      setUsers(res.data.users);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  }, [page, search]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handlePlanChange = async (userId, plan) => {
    setUpdatingId(userId);
    try {
      await api.patch(`/api/admin/users/${userId}/plan`, { plan });
      setUsers(users.map(u => u.id === userId ? { ...u, plan } : u));
      toast.success(`Plan updated to ${PLAN_LABELS[plan]}`);
    } catch {
      toast.error('Failed to update plan');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const trialDaysLeft = (trialStartedAt) => {
    if (!trialStartedAt) return null;
    const elapsed = (Date.now() - new Date(trialStartedAt).getTime()) / (1000 * 60 * 60 * 24);
    return Math.max(0, Math.ceil(14 - elapsed));
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  return (
    <div className="admin-page">
      <header className="admin-header">
        <div className="admin-header__left">
          <button className="admin-back" onClick={() => navigate('/dashboard')}>← Dashboard</button>
          <h1>Admin Panel</h1>
          <span className="admin-badge">Super Admin</span>
        </div>
      </header>

      {/* Stats */}
      <div className="admin-stats">
        {loadingStats ? (
          <div className="admin-loading">Loading stats…</div>
        ) : stats && (
          <>
            <div className="stat-card">
              <span className="stat-value">{stats.totalUsers}</span>
              <span className="stat-label">Total Users</span>
            </div>
            <div className="stat-card">
              <span className="stat-value">{stats.newUsersLast7Days}</span>
              <span className="stat-label">New (7 days)</span>
            </div>
            <div className="stat-card">
              <span className="stat-value">{stats.totalMeetings}</span>
              <span className="stat-label">Meetings</span>
            </div>
            <div className="stat-card">
              <span className="stat-value">{stats.totalRecordings}</span>
              <span className="stat-label">Recordings</span>
            </div>
            <div className="stat-card">
              <span className="stat-value">{stats.newUsersLast30Days ?? '—'}</span>
              <span className="stat-label">New (30 days)</span>
            </div>
            <div className="stat-card">
              <span className="stat-value">{stats.activeHosts ?? '—'}</span>
              <span className="stat-label">Active Hosts</span>
            </div>
            <div className="stat-card">
              <span className="stat-value">
                {stats.conversion?.paid ?? 0}
                {stats.conversion?.rate != null && (
                  <span style={{ fontSize: '0.5em', opacity: 0.7, marginLeft: 6 }}>
                    ({stats.conversion.rate}%)
                  </span>
                )}
              </span>
              <span className="stat-label">Paid Users · Conversion</span>
            </div>
            <div className="stat-card stat-card--plan">
              <span className="stat-label">Signup Method</span>
              <div className="plan-breakdown">
                <div className="plan-breakdown__row">
                  <span className="mini-plan-badge plan-badge--pro">Google</span>
                  <span className="plan-breakdown__count">{stats.signupMethod?.google ?? 0} users</span>
                </div>
                <div className="plan-breakdown__row">
                  <span className="mini-plan-badge plan-badge--trial">Email</span>
                  <span className="plan-breakdown__count">{stats.signupMethod?.email ?? 0} users</span>
                </div>
              </div>
            </div>
            <div className="stat-card stat-card--plan">
              <span className="stat-label">Plan Breakdown</span>
              <div className="plan-breakdown">
                {Object.entries(stats.planBreakdown || {}).map(([plan, count]) => (
                  <div key={plan} className="plan-breakdown__row">
                    <span className={`mini-plan-badge plan-badge--${plan}`}>{PLAN_LABELS[plan]}</span>
                    <span className="plan-breakdown__count">{count} users</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="stat-card">
              <span className="stat-value">{stats.meetingsLast7Days ?? '—'}</span>
              <span className="stat-label">Meetings (7 days)</span>
            </div>
            <div className="stat-card">
              <span className="stat-value">{stats.recordingsLast7Days ?? '—'}</span>
              <span className="stat-label">Recordings (7 days)</span>
            </div>
          </>
        )}
      </div>

      {/* Signup trend — last 30 days */}
      {!loadingStats && stats?.signupTrend?.length > 0 && (
        <div className="admin-section">
          <div className="admin-section__header">
            <h2>Signups — last 30 days <span className="total-count">({stats.newUsersLast30Days} total)</span></h2>
          </div>
          <div className="signup-trend">
            {(() => {
              const maxCount = Math.max(...stats.signupTrend.map(d => d.count), 1);
              return stats.signupTrend.map((d) => (
                <div key={d.day} className="signup-trend__bar-wrap" title={`${d.day}: ${d.count} signup${d.count !== 1 ? 's' : ''}`}>
                  <div
                    className="signup-trend__bar"
                    style={{ height: `${Math.max((d.count / maxCount) * 100, 6)}%` }}
                  >
                    <span className="signup-trend__count">{d.count}</span>
                  </div>
                  <span className="signup-trend__date">{d.day.slice(5)}</span>
                </div>
              ));
            })()}
          </div>
        </div>
      )}

      {/* User Management */}
      <div className="admin-section">
        <div className="admin-section__header">
          <h2>User Management <span className="total-count">({total})</span></h2>
          <form onSubmit={handleSearch} className="admin-search">
            <input
              type="text"
              placeholder="Search by name or email…"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              className="admin-search__input"
            />
            <button type="submit" className="admin-search__btn">Search</button>
            {search && (
              <button type="button" className="admin-search__clear"
                onClick={() => { setSearch(''); setSearchInput(''); setPage(1); }}>
                Clear
              </button>
            )}
          </form>
        </div>

        {loadingUsers ? (
          <div className="admin-loading">Loading users…</div>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Joined</th>
                  <th>Plan</th>
                  <th>Trial Days Left</th>
                  <th>Auth</th>
                  <th>Change Plan</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} className={u.is_admin ? 'row--admin' : ''}>
                    <td className="cell-user">
                      {u.avatar_url
                        ? <img src={u.avatar_url} alt="" className="admin-avatar" />
                        : <div className="admin-avatar admin-avatar--initials">{u.name?.charAt(0).toUpperCase()}</div>
                      }
                      <div>
                        <div className="cell-user__name">
                          {u.name}
                          {u.is_admin && <span className="admin-tag">Admin</span>}
                        </div>
                        <div className="cell-user__email">{u.email}</div>
                      </div>
                    </td>
                    <td>{formatDate(u.created_at)}</td>
                    <td>
                      <span className={`mini-plan-badge plan-badge--${u.plan}`}>
                        {PLAN_LABELS[u.plan] || u.plan}
                      </span>
                    </td>
                    <td>
                      {u.plan === 'trial'
                        ? (() => {
                            const d = trialDaysLeft(u.trial_started_at);
                            return <span className={`days-left ${d <= 3 ? 'days-left--urgent' : d <= 7 ? 'days-left--warn' : ''}`}>{d}d</span>;
                          })()
                        : '—'}
                    </td>
                    <td>
                      <span className={`auth-badge ${u.has_google ? 'auth-badge--google' : 'auth-badge--email'}`}>
                        {u.has_google ? 'Google' : 'Email'}
                      </span>
                    </td>
                    <td>
                      <select
                        className="plan-select"
                        value={u.plan}
                        disabled={updatingId === u.id}
                        onChange={e => handlePlanChange(u.id, e.target.value)}
                      >
                        {PLAN_OPTIONS.map(p => (
                          <option key={p} value={p}>{PLAN_LABELS[p]}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="admin-pagination">
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
            <span>Page {page} of {pages}</span>
            <button disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Next →</button>
          </div>
        )}
      </div>
    </div>
  );
}
